<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <SevaTopbar
        app-name="OnePlaceHQ"
        product-name="Seva"
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        search-placeholder="Search patrons, donations..."
        @select-patron="handleSelectPatron"
        @select-donation="handleSelectDonation"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="messages-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('messages.title') }}</h1>
          <p class="page-subtitle">{{ $t('messages.subtitle') }}</p>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-bar">
        <div class="filter-field">
          <label>{{ $t('messages.status') }}</label>
          <select v-model="statusFilter" class="filter-select" @change="handleFilterChange">
            <option value="">{{ $t('messages.allStatuses') }}</option>
            <option value="pending">{{ $t('messages.statusPending') }}</option>
            <option value="sent">{{ $t('messages.statusSent') }}</option>
            <option value="delivered">{{ $t('messages.statusDelivered') }}</option>
            <option value="read">{{ $t('messages.statusRead') }}</option>
            <option value="failed">{{ $t('messages.statusFailed') }}</option>
          </select>
        </div>

        <div class="filter-field">
          <IntercomDateInput
            v-model="dateFrom"
            :label="$t('filters.fromDate')"
            class="date-input"
            @update:model-value="handleFilterChange"
          />
        </div>

        <div class="filter-field">
          <IntercomDateInput
            v-model="dateTo"
            :label="$t('filters.toDate')"
            class="date-input"
            @update:model-value="handleFilterChange"
          />
        </div>

        <div class="filter-field patron-filter">
          <label>{{ $t('messages.filterByPatron') }}</label>
          <div class="patron-autocomplete">
            <div class="autocomplete-input-wrapper">
              <input
                type="text"
                v-model="patronSearchQuery"
                :placeholder="$t('messages.searchPatron')"
                class="filter-input"
                @input="handlePatronSearch"
                @focus="showPatronDropdown = patronSearchResults.length > 0"
                @blur="hidePatronDropdown"
              />
              <button
                v-if="selectedPatron"
                class="clear-btn"
                @click.prevent="clearPatronFilter"
                type="button"
              >
                ×
              </button>
            </div>
            <div v-if="showPatronDropdown" class="autocomplete-dropdown">
              <div v-if="patronSearchLoading" class="dropdown-loading">
                {{ $t('common.loading') }}...
              </div>
              <div
                v-else
                v-for="patron in patronSearchResults"
                :key="patron.id"
                class="dropdown-item"
                @mousedown.prevent="selectPatron(patron)"
              >
                <span class="patron-name">{{ patron.name }}</span>
                <span class="patron-phone">{{ patron.phone }}</span>
              </div>
            </div>
          </div>
        </div>

        <button v-if="hasActiveFilters" class="reset-all-btn" @click="resetAllFilters">
          {{ $t('filters.clearFilters') || 'Reset all' }}
        </button>
      </div>

      <!-- Messages Table -->
      <IntercomCard class="messages-table-card">
        <IntercomTable
          :columns="columns"
          :data="messages"
          :loading="loading"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          hoverable
          clickable
          @sort="handleSort"
          @row-click="toggleRow"
        >
          <!-- Recipient Cell - show name + phone -->
          <template #cell-recipient_phone="{ row }">
            <div class="recipient-info">
              <span v-if="row.recipient_name" class="recipient-name">{{ row.recipient_name }}</span>
              <span class="phone-number" :class="{ 'has-name': row.recipient_name }">{{ formatPhone(row.recipient_phone) }}</span>
            </div>
          </template>

          <!-- Channel Cell -->
          <template #cell-channel_name="{ value }">
            <span class="channel-badge" :class="value">
              {{ formatChannel(value) }}
            </span>
          </template>

          <!-- Status Cell -->
          <template #cell-status="{ value }">
            <span class="status-badge" :class="value">
              {{ $t(`messages.status${capitalize(value)}`) }}
            </span>
          </template>

          <!-- Sent At Cell -->
          <template #cell-created_at="{ row }">
            {{ formatDateTime(row.sent_at || row.created_at) }}
          </template>

          <!-- Empty State -->
          <template #empty>
            <div class="empty-state">
              <div class="empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <h3>{{ $t('messages.noMessages') }}</h3>
              <p class="empty-hint">{{ $t('messages.noMessagesHint') }}</p>
            </div>
          </template>
        </IntercomTable>

        <!-- Pagination -->
        <div class="pagination" v-if="totalCount > limit">
          <div class="pagination-info">
            {{ $t('messages.showing', { from: offset + 1, to: Math.min(offset + limit, totalCount), total: totalCount }) }}
          </div>
          <div class="pagination-controls">
            <button
              class="pagination-btn"
              :disabled="offset === 0"
              @click="goToPage('prev')"
            >
              &larr; {{ $t('common.previous') }}
            </button>
            <button
              class="pagination-btn"
              :disabled="offset + limit >= totalCount"
              @click="goToPage('next')"
            >
              {{ $t('common.next') }} &rarr;
            </button>
          </div>
        </div>
      </IntercomCard>
    </div>

    <!-- Slide-over Panel for Message Details -->
    <Transition name="slide-over">
      <div v-if="selectedMessage" class="slide-over-backdrop" @click="selectedMessage = null">
        <div class="slide-over-panel" @click.stop>
          <div class="detail-header">
            <h3>{{ $t('messages.messageDetails') }}</h3>
            <button class="close-btn" @click="selectedMessage = null">×</button>
          </div>
          <div class="detail-content">
            <!-- Recipient -->
            <div class="detail-row">
              <label>{{ $t('messages.recipient') }}</label>
              <div class="detail-value">
                <span v-if="selectedMessage.recipient_name" class="recipient-name">{{ selectedMessage.recipient_name }}</span>
                <span class="phone-number">{{ formatPhone(selectedMessage.phone || selectedMessage.recipient_phone) }}</span>
              </div>
            </div>

            <!-- Channel & Direction -->
            <div class="detail-row">
              <label>{{ $t('messages.channel') }}</label>
              <div class="channel-direction">
                <span class="channel-badge" :class="selectedMessage.channel_name">
                  {{ formatChannel(selectedMessage.channel_name) }}
                </span>
                <span v-if="selectedMessage.direction" class="direction-badge" :class="selectedMessage.direction">
                  {{ selectedMessage.direction === 'outbound' ? 'Outgoing' : 'Incoming' }}
                </span>
              </div>
            </div>

            <!-- Status -->
            <div class="detail-row">
              <label>{{ $t('messages.status') }}</label>
              <span class="status-badge" :class="selectedMessage.status">
                {{ $t(`messages.status${capitalize(selectedMessage.status)}`) }}
              </span>
            </div>

            <!-- Template or Message Content -->
            <div class="detail-row" v-if="selectedMessage.template_name">
              <label>{{ $t('messages.template') }}</label>
              <span>{{ selectedMessage.template_name }}</span>
            </div>
            <div class="detail-row" v-if="selectedMessage.content">
              <label>{{ $t('messages.messageContent') }}</label>
              <p class="message-text-full">{{ selectedMessage.content }}</p>
            </div>

            <!-- Media Attachment -->
            <div class="detail-row" v-if="selectedMessage.media_url">
              <label>{{ $t('messages.attachment') }}</label>
              <a :href="selectedMessage.media_url" target="_blank" class="attachment-link">
                {{ selectedMessage.media_type || 'View attachment' }}
              </a>
            </div>

            <!-- Error Details -->
            <div class="detail-row error-row" v-if="selectedMessage.status === 'failed' && selectedMessage.error_message">
              <label>{{ $t('messages.errorDetails') }}</label>
              <p class="error-text">{{ selectedMessage.error_message }}</p>
            </div>

            <!-- Triggered By -->
            <div class="detail-row" v-if="selectedMessage.source_action">
              <label>{{ $t('messages.triggeredBy') }}</label>
              <span>{{ formatSourceAction(selectedMessage.source_action) }}</span>
            </div>

            <!-- Timestamps -->
            <div class="detail-meta">
              <div class="meta-item">
                <label>{{ $t('messages.created') }}</label>
                <span>{{ formatDateTime(selectedMessage.created_at) }}</span>
              </div>
              <div class="meta-item" v-if="selectedMessage.sent_at">
                <label>{{ $t('messages.sent') }}</label>
                <span>{{ formatDateTime(selectedMessage.sent_at) }}</span>
              </div>
              <div class="meta-item" v-if="selectedMessage.delivered_at">
                <label>{{ $t('messages.delivered') }}</label>
                <span>{{ formatDateTime(selectedMessage.delivered_at) }}</span>
              </div>
              <div class="meta-item" v-if="selectedMessage.read_at">
                <label>{{ $t('messages.read') }}</label>
                <span>{{ formatDateTime(selectedMessage.read_at) }}</span>
              </div>
              <div class="meta-item" v-if="selectedMessage.external_id">
                <label>{{ $t('messages.messageId') }}</label>
                <span class="mono">{{ selectedMessage.external_id }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useSeva } from '../composables/useSeva.js'
import { useNavigation } from '../composables/useNavigation.js'
import { IntercomLayout, IntercomSidebar, IntercomCard, IntercomTable, IntercomDateInput } from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import dayjs from 'dayjs'

const router = useRouter()
const { t } = useI18n()
const { user, globalUser, logout } = useAuth()
const { getMessageLogs, listPatrons } = useSeva()

const { navSections } = useNavigation()

// Default filter values
const defaultFromDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD')
const defaultToDate = dayjs().format('YYYY-MM-DD')

// State
const loading = ref(true)
const messages = ref([])
const totalCount = ref(0)
const limit = ref(50)
const offset = ref(0)
const selectedMessage = ref(null)
const expandedRowId = ref(null)

// Filters
const statusFilter = ref('')
const dateFrom = ref(defaultFromDate)
const dateTo = ref(defaultToDate)
const phoneFilter = ref('')

// Computed - check if any filter is active
const hasActiveFilters = computed(() =>
  statusFilter.value !== '' ||
  dateFrom.value !== defaultFromDate ||
  dateTo.value !== defaultToDate ||
  selectedPatron.value !== null
)

// Patron autocomplete
const patronSearchQuery = ref('')
const patronSearchResults = ref([])
const showPatronDropdown = ref(false)
const selectedPatron = ref(null)
const patronSearchLoading = ref(false)

let patronSearchTimeout = null
const handlePatronSearch = async () => {
  const query = patronSearchQuery.value.trim()
  if (query.length < 2) {
    patronSearchResults.value = []
    showPatronDropdown.value = false
    return
  }

  if (patronSearchTimeout) clearTimeout(patronSearchTimeout)
  patronSearchTimeout = setTimeout(async () => {
    try {
      patronSearchLoading.value = true
      const result = await listPatrons({ search: query, limit: 10 })
      patronSearchResults.value = (result.data || []).map(p => ({
        id: p.id,
        name: p.name,
        phone: p.phone,
        label: `${p.name} (${p.phone})`
      }))
      showPatronDropdown.value = patronSearchResults.value.length > 0
    } catch (error) {
      console.error('Patron search failed:', error)
      patronSearchResults.value = []
    } finally {
      patronSearchLoading.value = false
    }
  }, 300)
}

const selectPatron = (patron) => {
  selectedPatron.value = patron
  patronSearchQuery.value = patron.name
  phoneFilter.value = patron.phone
  showPatronDropdown.value = false
  handleFilterChange()
}

const clearPatronFilter = () => {
  selectedPatron.value = null
  patronSearchQuery.value = ''
  phoneFilter.value = ''
  patronSearchResults.value = []
  showPatronDropdown.value = false
  handleFilterChange()
}

// Filter clear methods
const clearFromDate = () => {
  dateFrom.value = defaultFromDate
  handleFilterChange()
}

const clearToDate = () => {
  dateTo.value = defaultToDate
  handleFilterChange()
}

const resetAllFilters = () => {
  statusFilter.value = ''
  dateFrom.value = defaultFromDate
  dateTo.value = defaultToDate
  selectedPatron.value = null
  patronSearchQuery.value = ''
  phoneFilter.value = ''
  patronSearchResults.value = []
  showPatronDropdown.value = false
  handleFilterChange()
}

const hidePatronDropdown = () => {
  setTimeout(() => {
    showPatronDropdown.value = false
  }, 200)
}

// Sorting - default to newest first
const sortBy = ref('created_at')
const sortOrder = ref('desc')

// Computed
const businessName = computed(() => user.value?.business?.name || '')

// Table columns
const columns = computed(() => [
  { key: 'recipient_phone', title: t('messages.recipient'), width: '150px', sortable: true, sortKey: 'recipient_phone' },
  { key: 'template_name', title: t('messages.template'), width: '150px', sortable: true, sortKey: 'template_name' },
  { key: 'channel_name', title: t('messages.channel'), width: '100px' },
  { key: 'status', title: t('messages.status'), width: '100px', sortable: true, sortKey: 'status' },
  { key: 'created_at', title: t('messages.sentAt'), width: '180px', sortable: true, sortKey: 'created_at' },
])

// Methods
const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : ''

const formatPhone = (phone) => {
  if (!phone) return '-'
  // Remove country code prefix if present
  const cleaned = phone.replace(/^\+91/, '')
  // Format as XXX XXX XXXX for Indian phones
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
  }
  return phone
}

const formatChannel = (channel) => {
  const channels = {
    whatsapp: 'WhatsApp',
    sms: 'SMS',
    email: 'Email',
    instagram: 'Instagram',
  }
  return channels[channel] || channel
}

const formatSourceAction = (action) => {
  const actions = {
    reminder: 'Reminder',
    birthday_reminder: 'Birthday Reminder',
    anniversary_reminder: 'Anniversary Reminder',
    lapsed_donor_reminder: 'Lapsed Donor Reminder',
    donation_thanks: 'Donation Thank You',
    '80g_receipt': '80G Receipt',
    manual: 'Manual Send',
  }
  return actions[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const formatDateTime = (date) => {
  if (!date) return '-'
  return dayjs(date).format('DD MMM YYYY, h:mm A')
}

const toggleRow = (row) => {
  // Toggle selection - if same row is clicked, deselect it
  if (selectedMessage.value?.id === row.id) {
    selectedMessage.value = null
    expandedRowId.value = null
  } else {
    selectedMessage.value = row
    expandedRowId.value = row.id
  }
}

const fetchMessages = async () => {
  try {
    loading.value = true
    const params = {
      limit: limit.value,
      offset: offset.value,
      sort_by: sortBy.value,
      sort_order: sortOrder.value,
    }
    if (statusFilter.value) params.status = statusFilter.value
    if (dateFrom.value) params.from_date = dateFrom.value
    if (dateTo.value) params.to_date = dateTo.value
    if (phoneFilter.value) params.phone = phoneFilter.value

    const response = await getMessageLogs(params)
    messages.value = response.data || []
    totalCount.value = response.pagination?.total || 0
  } catch (error) {
    console.error('Failed to fetch messages:', error)
  } finally {
    loading.value = false
  }
}

const handleSort = ({ sortBy: newSort, sortOrder: newOrder }) => {
  sortBy.value = newSort
  sortOrder.value = newOrder
  offset.value = 0 // Reset to first page on sort
  fetchMessages()
}

const handleFilterChange = () => {
  offset.value = 0 // Reset to first page on filter change
  fetchMessages()
}

const goToPage = (direction) => {
  if (direction === 'prev' && offset.value > 0) {
    offset.value = Math.max(0, offset.value - limit.value)
  } else if (direction === 'next' && offset.value + limit.value < totalCount.value) {
    offset.value = offset.value + limit.value
  }
  fetchMessages()
}

const handleSelectPatron = (patron) => {
  router.push(`/patrons/${patron.id}`)
}

const handleSelectDonation = (donation) => {
  router.push(`/donations/${donation.id}`)
}

const handleNotifications = () => {
  // Handle notifications click
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchMessages()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.messages-view {
  padding: var(--space-6);
  max-width: 1400px;
}

.page-header {
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin: var(--space-1) 0 0 0;
}

.filters-bar {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  flex-wrap: wrap;
  align-items: flex-end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.filter-field label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.filter-select,
.filter-input,
.date-input {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  min-width: 140px;
}

.filter-select:focus,
.filter-input:focus,
.date-input:focus {
  outline: none;
  border-color: var(--color-blue);
}

/* Filter input wrapper with clear button */
.filter-input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.filter-input-wrapper .date-input {
  padding-right: var(--space-8);
}

.filter-input-wrapper > .clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--color-gray-200);
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  color: var(--color-gray-600);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color 0.15s, color 0.15s;
}

.filter-input-wrapper > .clear-btn:hover {
  background: var(--color-gray-300);
  color: var(--color-charcoal);
}

.reset-all-btn {
  background: none;
  border: none;
  color: var(--color-blue);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  padding: var(--space-2) var(--space-3);
  white-space: nowrap;
  align-self: flex-end;
}

.reset-all-btn:hover {
  text-decoration: underline;
}

.patron-filter {
  flex: 1;
  min-width: 220px;
  max-width: 300px;
}

.patron-autocomplete {
  position: relative;
}

.autocomplete-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.autocomplete-input-wrapper .filter-input {
  width: 100%;
  padding-right: var(--space-8);
}

.autocomplete-input-wrapper .clear-btn {
  position: absolute;
  right: var(--space-2);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-gray-200);
  border: none;
  border-radius: var(--radius-full);
  font-size: 14px;
  color: var(--color-gray-600);
  cursor: pointer;
  transition: all 0.2s;
}

.autocomplete-input-wrapper .clear-btn:hover {
  background: var(--color-gray-300);
  color: var(--color-gray-800);
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: var(--space-1);
  background: white;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 240px;
  overflow-y: auto;
}

.dropdown-loading {
  padding: var(--space-3);
  text-align: center;
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

.dropdown-item {
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-bottom: 1px solid var(--color-gray-100);
  transition: background 0.15s;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: var(--color-gray-50);
}

.dropdown-item .patron-name {
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
  font-size: var(--font-size-sm);
}

.dropdown-item .patron-phone {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  font-family: var(--font-mono);
}

.messages-table-card {
  overflow: hidden;
}

.phone-number {
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
}

.channel-badge {
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.channel-badge.whatsapp {
  background: #dcfce7;
  color: #16a34a;
}

.channel-badge.sms {
  background: #e0f2fe;
  color: #0284c7;
}

.channel-badge.email {
  background: #fef3c7;
  color: #d97706;
}

/* Channel & Direction in details panel */
.channel-direction {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.direction-badge {
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.direction-badge.outbound {
  background: #e0f2fe;
  color: #0284c7;
}

.direction-badge.inbound {
  background: #f3e8ff;
  color: #9333ea;
}

.attachment-link {
  color: var(--color-blue);
  text-decoration: none;
  font-size: var(--font-size-sm);
}

.attachment-link:hover {
  text-decoration: underline;
}

.status-badge {
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.status-badge.pending {
  background: var(--color-gray-100);
  color: var(--color-gray-600);
}

.status-badge.sent {
  background: #e0f2fe;
  color: #0284c7;
}

.status-badge.delivered {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge.read {
  background: #dcfce7;
  color: #15803d;
}

.status-badge.failed {
  background: var(--color-error-bg);
  color: var(--color-error);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: var(--space-12);
}

.empty-icon {
  color: var(--color-gray-300);
  margin-bottom: var(--space-4);
}

.empty-state h3 {
  font-size: var(--font-size-lg);
  color: var(--color-charcoal);
  margin: 0;
}

.empty-hint {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-top: var(--space-2);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}

.pagination-info {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.pagination-controls {
  display: flex;
  gap: var(--space-2);
}

.pagination-btn {
  padding: var(--space-2) var(--space-3);
  background: white;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--color-gray-50);
  border-color: var(--color-gray-400);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Recipient Info - name + phone */
.recipient-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.recipient-name {
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.phone-number.has-name {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

/* Slide-over Drawer */
.slide-over-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 50;
}

.slide-over-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 90vw;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Slide-over Transition */
.slide-over-enter-active,
.slide-over-leave-active {
  transition: all 0.3s ease;
}

.slide-over-enter-active .slide-over-panel,
.slide-over-leave-active .slide-over-panel {
  transition: transform 0.3s ease;
}

.slide-over-enter-from,
.slide-over-leave-to {
  background: rgba(0, 0, 0, 0);
}

.slide-over-enter-from .slide-over-panel,
.slide-over-leave-to .slide-over-panel {
  transform: translateX(100%);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-4) var(--space-5);
  background: white;
  border-bottom: 1px solid var(--color-gray-200);
  flex-shrink: 0;
}

.detail-header h3 {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.close-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  font-size: 20px;
  color: var(--color-gray-400);
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--color-gray-100);
  color: var(--color-gray-600);
}

.detail-content {
  padding: var(--space-5);
  flex: 1;
  overflow-y: auto;
}

.detail-row {
  margin-bottom: var(--space-4);
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-row label {
  display: block;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: var(--space-1);
}

.detail-value {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.message-text-full {
  margin: 0;
  padding: var(--space-3);
  background: white;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  line-height: 1.6;
  white-space: pre-wrap;
  color: var(--color-charcoal);
}

.error-row .error-text {
  margin: 0;
  padding: var(--space-3);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-error);
}

.detail-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--space-3);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.meta-item label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  margin: 0;
}

.meta-item span {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
}

.meta-item .mono {
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  word-break: break-all;
}

@media (max-width: 768px) {
  .messages-view {
    padding: var(--space-4);
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-field {
    width: 100%;
  }

  .patron-filter {
    max-width: none;
  }

  .messages-table-card {
    overflow-x: auto;
  }

  .pagination {
    flex-direction: column;
    gap: var(--space-3);
  }

  .detail-meta {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
